import { FC, useId, useRef, useState } from "react";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  ExportCsv,
  ExportExcel,
  ToolbarButton,
  useGridApiContext
} from "@mui/x-data-grid-premium";

// Excel ignores a column's valueFormatter and writes date columns as dd.mm.yyyy by default.
// The slashes are escaped so they stay literal: unescaped, Excel swaps them for the viewer's
// locale date separator, which is a dash in Danish.
const EXCEL_DATE_FORMAT = { numFmt: "dd\\/mm\\/yyyy" };

type Props = {
  // Base name of the downloaded file. Today's date is appended so repeated
  // exports don't overwrite each other in the downloads folder.
  fileName: string;
};

// Toolbar button opening a CSV / Excel download menu. Both exports take the rows
// as currently filtered and sorted, and each column's value as its valueGetter
// returns it (so the progress columns export their label, not the icon).
export const ExportMenu: FC<Props> = ({ fileName }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Unique per grid, since a page can mount more than one overview grid.
  const id = useId();
  const triggerId = `${id}-export-trigger`;
  const menuId = `${id}-export-menu`;

  const apiRef = useGridApiContext();

  const datedFileName = `${fileName} ${new Date().toISOString().slice(0, 10)}`;
  const close = () => setOpen(false);

  // Read when the menu renders open, so it reflects the columns the grid has at that point.
  const excelDateStyles = open
    ? Object.fromEntries(
        apiRef.current
          .getAllColumns()
          .filter((column) => column.type === "date")
          .map((column) => [column.field, EXCEL_DATE_FORMAT])
      )
    : undefined;

  return (
    <>
      <Tooltip title="Export">
        <ToolbarButton
          ref={triggerRef}
          id={triggerId}
          aria-label="Export"
          aria-controls={menuId}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={() => setOpen(true)}
        >
          <FileDownloadIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>
      <Menu
        id={menuId}
        anchorEl={triggerRef.current}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ list: { "aria-labelledby": triggerId } }}
      >
        <ExportCsv
          render={<MenuItem />}
          options={{ fileName: datedFileName, utf8WithBom: true }}
          onClick={close}
        >
          Download as CSV
        </ExportCsv>
        <ExportExcel
          render={<MenuItem />}
          options={{ fileName: datedFileName, columnsStyles: excelDateStyles }}
          onClick={close}
        >
          Download as Excel
        </ExportExcel>
      </Menu>
    </>
  );
};
